import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { Toaster } from 'sonner';
import '@/i18n';  // initialise translations early
import '@/lib/agGridEnterprise';  // Iter-164 — register AG Grid modules once globally

import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import TrialSignupPage from '@/pages/TrialSignupPage';
import AuthCallback from '@/pages/AuthCallback';
import PlaceholderPage from '@/pages/PlaceholderPage';
import AppLayout from '@/components/layout/AppLayout';
import WithAgentBanner from '@/components/layout/WithAgentBanner';
import AgentChatPanel from '@/components/AgentChatPanel';
import { PRE_AWARD_MODULES, CONTRACTS_MODULES, CLAIMS_LD_MODULES } from '@/config/navigation';

/* ----- Iter-159 — code-split heavy pages via React.lazy ----- */
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ContractsPage = lazy(() => import('@/pages/ContractsPage'));
const ContractDetailPage = lazy(() => import('@/pages/ContractDetailPage'));
const ContractWizard = lazy(() => import('@/pages/ContractWizard'));
const ContractDraftPage = lazy(() => import('@/pages/ContractDraftPage'));
const ContractNegotiationPage = lazy(() => import('@/pages/ContractNegotiationPage'));
const ContractPerformancePage = lazy(() => import('@/pages/ContractPerformancePage'));
const CMPortfolioPage = lazy(() => import('@/pages/CMPortfolioPage'));
const ContractTypesReferencePage = lazy(() => import('@/pages/ContractTypesReferencePage'));
const ContractIntegrationsPage = lazy(() => import('@/pages/ContractIntegrationsPage'));
const ClauseLibraryPage = lazy(() => import('@/pages/ClauseLibraryPage'));
const TemplateBuilderPage = lazy(() => import('@/pages/TemplateBuilderPage'));
const ClaimsPage = lazy(() => import('@/pages/ClaimsPage'));
const ClaimDetailPage = lazy(() => import('@/pages/ClaimDetailPage'));
const ClaimsLDDrilldowns = lazy(() => import('@/pages/cld/ClaimsLDDrilldowns'));
const ClaimsLDAuthoring = lazy(() => import('@/pages/cld/ClaimsLDAuthoring'));
const CldV2Workbench = lazy(() => import('@/pages/cld/CldV2Workbench'));
const CldPortfolioAnalytics = lazy(() => import('@/pages/cld/CldPortfolioAnalytics'));
const LDClauseLibraryPage = lazy(() => import('@/pages/cld/LDClauseLibraryPage'));
const LDNoticeDispatchPage = lazy(() => import('@/pages/cld/LDNoticeDispatchPage'));
const AdjudicationCaseRoomPage = lazy(() => import('@/pages/cld/AdjudicationCaseRoomPage'));
const PredictiveRiskPage = lazy(() => import('@/pages/cld/PredictiveRiskPage'));
const AdjudicationListPage = lazy(() => import('@/pages/cld/AdjudicationListPage'));
const FmNoticeV2 = lazy(() => import('@/pages/cld/FmNoticeV2'));
const EvidenceRepoV2 = lazy(() => import('@/pages/cld/EvidenceRepoV2'));
const XmodHealthDashboard = lazy(() => import('@/pages/admin/XmodHealthDashboard'));
const AuditTrailExplorerPage = lazy(() => import('@/pages/admin/AuditTrailExplorerPage'));
const CxoDashboardPage = lazy(() => import('@/pages/CxoDashboardPage'));
const PortalContractsPage = lazy(() => import('@/pages/portal/PortalContractsPage'));
const LDManagementPage = lazy(() => import('@/pages/LDManagementPage'));
const VariationsPage = lazy(() => import('@/pages/VariationsPage'));
const ObligationsPage = lazy(() => import('@/pages/ObligationsPage'));
const MilestonesPage = lazy(() => import('@/pages/MilestonesPage'));
const CorrespondencePage = lazy(() => import('@/pages/CorrespondencePage'));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage'));
const RisksPage = lazy(() => import('@/pages/RisksPage'));
const VendorsPage = lazy(() => import('@/pages/VendorsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'));
const PreAwardPage = lazy(() => import('@/pages/PreAward/PreAwardPage'));
const StrategyDetailPage = lazy(() => import('@/pages/PreAward/StrategyDetailPage'));
const TenderManagementPage = lazy(() => import('@/pages/PreAward/TenderManagementPage'));
const TaxonomyPage = lazy(() => import('@/pages/TaxonomyPage'));
const CategoryPerformancePage = lazy(() => import('@/pages/CategoryPerformancePage'));
const PRConsolidationPage = lazy(() => import('@/pages/PRConsolidationPage'));
const SpendAnalyticsPage = lazy(() => import('@/pages/SpendAnalyticsPage'));
const KraljicMatrixPage = lazy(() => import('@/pages/KraljicMatrixPage'));
const CategoryStrategyPage = lazy(() => import('@/pages/CategoryStrategyPage'));
const MarketIntelPage = lazy(() => import('@/pages/MarketIntelPage'));
const DemandPlanningPage = lazy(() => import('@/pages/DemandPlanningPage'));
const AgentsPage = lazy(() => import('@/pages/AgentsPage'));
const SupplierRegistrationPage = lazy(() => import('@/pages/SupplierRegistrationPage'));
const SupplierPortalPage = lazy(() => import('@/pages/SupplierPortalPage'));
const SupplierThreeSixtyPage = lazy(() => import('@/pages/SupplierThreeSixtyPage'));
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const InvoiceWorkbenchPage = lazy(() => import('@/pages/InvoiceWorkbenchPage'));
const InvoiceAdminPage = lazy(() => import('@/pages/InvoiceAdminPage'));
const MobileApprovalPage = lazy(() => import('@/pages/MobileApprovalPage'));
const CataloguePage = lazy(() => import('@/pages/CataloguePage'));
const QualificationPage = lazy(() => import('@/pages/QualificationPage'));
const PerformancePage = lazy(() => import('@/pages/PerformancePage'));
const OnboardingApprovalPage = lazy(() => import('@/pages/OnboardingApprovalPage'));
const PerformanceAutomationPage = lazy(() => import('@/pages/PerformanceAutomationPage'));
const SourcingEventsPage = lazy(() => import('@/pages/SourcingEventsPage'));
const SourcingEventWizard = lazy(() => import('@/pages/SourcingEventWizard'));
const ApprovalInboxPage = lazy(() => import('@/pages/sourcing/ApprovalInboxPage'));
const DOAMatrixAdminPage = lazy(() => import('@/pages/sourcing/DOAMatrixAdminPage'));
const SourcingCalendarPage = lazy(() => import('@/pages/sourcing/SourcingCalendarPage'));
const LongListReviewPage = lazy(() => import('@/pages/sourcing/LongListReviewPage'));
const EventTypeProfilesAdminPage = lazy(() => import('@/pages/sourcing/EventTypeProfilesAdminPage'));
const SourcingL2StubPage = lazy(() => import('@/pages/sourcing/SourcingL2StubPage'));
const ClarificationDashboard = lazy(() => import('@/pages/ClarificationDashboard'));
const ClarificationHubPage = lazy(() => import('@/pages/sourcing/ClarificationHubPage'));
const BidMonitoringPage = lazy(() => import('@/pages/sourcing/BidMonitoringPage'));
const PortalBidSubmissionPage = lazy(() => import('@/pages/portal/PortalBidSubmissionPage'));
const SRMExtrasPage = lazy(() => import('@/pages/SRMExtrasPage'));
const SourcingExtrasPage = lazy(() => import('@/pages/SourcingExtrasPage'));
const RequisitionsExtrasPage = lazy(() => import('@/pages/RequisitionsExtrasPage'));
const POExtrasPage = lazy(() => import('@/pages/POExtrasPage'));
const GRExtrasPage = lazy(() => import('@/pages/GRExtrasPage'));
const BridgeExtrasPage = lazy(() => import('@/pages/BridgeExtrasPage'));
const PortalInvoicesPage = lazy(() => import('@/pages/portal/PortalInvoicesPage'));
const WorkflowsAdminPage = lazy(() => import('@/pages/WorkflowsAdminPage'));
const PortalCAPAPage = lazy(() => import('@/pages/portal/PortalCAPAPage'));
const PortalSourcingL2 = lazy(() => import('@/pages/portal/PortalSourcingL2'));
const BidOpeningPage = lazy(() => import('@/pages/sourcing/BidOpeningPage'));
const BidOpeningLandingPage = lazy(() => import('@/pages/sourcing/BidOpeningLandingPage'));
const AuctionPage = lazy(() => import('@/pages/sourcing/AuctionPage'));
const BAFOPage = lazy(() => import('@/pages/sourcing/BAFOPage'));
const FrameworkPage = lazy(() => import('@/pages/sourcing/FrameworkPage'));
const SourcingL2Landings = lazy(() => import('@/pages/sourcing/SourcingL2Landings'));
const SourcingCockpitPage = lazy(() => import('@/pages/sourcing/SourcingCockpitPage'));
const StandupPreferencesPage = lazy(() => import('@/pages/sourcing/StandupPreferencesPage'));
const DirectAwardLandingPage = lazy(() => import('@/pages/sourcing/DirectAwardLandingPage'));
const DirectAwardPage = lazy(() => import('@/pages/sourcing/DirectAwardPage'));
const AwardRecommendationLandingPage = lazy(() => import('@/pages/sourcing/AwardRecommendationLandingPage'));
const AwardRecommendationPage = lazy(() => import('@/pages/sourcing/AwardRecommendationPage'));
const AwardDOAMatrixPage = lazy(() => import('@/pages/sourcing/AwardDOAMatrixPage'));
const ImportMasterDataPage = lazy(() => import('@/pages/admin/ImportMasterDataPage'));
const ERPConnectorsPage = lazy(() => import('@/pages/ERPConnectorsPage'));
const ClaimInitiationPage = lazy(() => import('@/pages/claims/ClaimInitiationPage'));
const ChangeOrderWizardPage = lazy(() => import('@/pages/contracts/ChangeOrderWizardPage'));
const CommunicationEnginePage = lazy(() => import('@/pages/sourcing/CommunicationEnginePage'));
const SourcingAnalyticsPage = lazy(() => import('@/pages/sourcing/SourcingAnalyticsPage'));
const SupplierPortalAdminPage = lazy(() => import('@/pages/sourcing/SupplierPortalAdminPage'));
const IttCompliancePage = lazy(() => import('@/pages/sourcing/IttCompliancePage'));
const SupplierPortalV2Page = lazy(() => import('@/pages/portal-v2/SupplierPortalV2Page'));
const SP10Login = lazy(() => import('@/pages/sp10/SP10Login'));
const SP10Layout = lazy(() => import('@/pages/sp10/SP10Layout'));
const SP10Dashboard = lazy(() => import('@/pages/sp10/SP10Dashboard'));
const SP10MyCompany = lazy(() => import('@/pages/sp10/SP10MyCompany'));
const SP10Sourcing = lazy(() => import('@/pages/sp10/SP10Sourcing'));
const SP10PurchaseOrders = lazy(() => import('@/pages/sp10/SP10PurchaseOrders'));
const SP10Deliveries = lazy(() => import('@/pages/sp10/SP10Deliveries'));
const SP10Invoices = lazy(() => import('@/pages/sp10/SP10Invoices'));
const SP10Contracts = lazy(() => import('@/pages/sp10/SP10Contracts'));
const SP10Claims = lazy(() => import('@/pages/sp10/SP10Claims'));
const SP10Catalogue = lazy(() => import('@/pages/sp10/SP10Catalogue'));
const SP10DemoGuide = lazy(() => import('@/pages/sp10/SP10DemoGuide'));
const EvaluationPage = lazy(() => import('@/pages/sourcing/EvaluationPage'));
const EvaluationLandingPage = lazy(() => import('@/pages/sourcing/EvaluationLandingPage'));
const EventTemplatesPage = lazy(() => import('@/pages/sourcing/EventTemplatesPage'));
const SupplierInvitationPage = lazy(() => import('@/pages/sourcing/SupplierInvitationPage'));
const AdminPanelPage = lazy(() => import('@/pages/AdminPanelPage'));
const SuperadminPortal = lazy(() => import('@/pages/SuperadminPortal'));
const IntegrationsAdminPage = lazy(() => import('@/pages/IntegrationsAdminPage'));
const InviteAcceptPage = lazy(() => import('@/pages/InviteAcceptPage'));
const FormConfigAdminPage = lazy(() => import('@/pages/FormConfigAdminPage'));
const DataLineagePage = lazy(() => import('@/pages/DataLineagePage'));
const ImpactAnalysisPage = lazy(() => import('@/pages/ImpactAnalysisPage'));
const RiskManagementPage = lazy(() => import('@/pages/RiskManagementPage'));
const DataGovernancePage = lazy(() => import('@/pages/DataGovernancePage'));
const DiversityProgrammeHubPage = lazy(() => import('@/pages/DiversityProgrammeHubPage'));
const CollaborationHubPage = lazy(() => import('@/pages/CollaborationHubPage'));
const RequisitionsPage = lazy(() => import('@/pages/RequisitionsPage'));
const PurchaseOrdersPage = lazy(() => import('@/pages/PurchaseOrdersPage'));
const GoodsReceiptPage = lazy(() => import('@/pages/GoodsReceiptPage'));
const P2PPage = lazy(() => import('@/pages/P2PPage'));
const SystemConfigPage = lazy(() => import('@/pages/SystemConfigPage'));
const SupplierQuestionnairePage = lazy(() => import('@/pages/SupplierQuestionnairePage'));
const AssessmentChecklistPage = lazy(() => import('@/pages/AssessmentChecklistPage'));
const RelationshipManagementPage = lazy(() => import('@/pages/RelationshipManagementPage'));

/* Named-export wrappers — lazy() requires a default; re-export via tiny shims */
const FMNoticePage = lazy(() => import('@/pages/cld/ClaimsLDDrilldowns').then(m => ({ default: m.FMNoticePage })));
const EvidenceRepoPage = lazy(() => import('@/pages/cld/ClaimsLDDrilldowns').then(m => ({ default: m.EvidenceRepoPage })));
const QuantumEnginePage = lazy(() => import('@/pages/cld/ClaimsLDDrilldowns').then(m => ({ default: m.QuantumEnginePage })));
const DisputeBoardPage = lazy(() => import('@/pages/cld/ClaimsLDDrilldowns').then(m => ({ default: m.DisputeBoardPage })));
const QuantumWorkbench = lazy(() => import('@/pages/cld/ClaimsLDAuthoring').then(m => ({ default: m.QuantumWorkbench })));
const NegotiationTable = lazy(() => import('@/pages/cld/ClaimsLDAuthoring').then(m => ({ default: m.NegotiationTable })));
const EnforcementBoard = lazy(() => import('@/pages/cld/ClaimsLDAuthoring').then(m => ({ default: m.EnforcementBoard })));
const RecoveryLedger = lazy(() => import('@/pages/cld/ClaimsLDAuthoring').then(m => ({ default: m.RecoveryLedger })));
const BidMonitoringHubPage = lazy(() => import('@/pages/sourcing/BidMonitoringPage').then(m => ({ default: m.default })));
const BidMonitoringDashboard = lazy(() => import('@/pages/sourcing/BidMonitoringPage').then(m => ({ default: m.BidMonitoringDashboard })));
const PortalAuctionPage = lazy(() => import('@/pages/portal/PortalSourcingL2').then(m => ({ default: m.PortalAuctionPage })));
const PortalBAFOPage = lazy(() => import('@/pages/portal/PortalSourcingL2').then(m => ({ default: m.PortalBAFOPage })));
const PortalFrameworkPage = lazy(() => import('@/pages/portal/PortalSourcingL2').then(m => ({ default: m.PortalFrameworkPage })));
const AuctionLandingPage = lazy(() => import('@/pages/sourcing/SourcingL2Landings').then(m => ({ default: m.AuctionLandingPage })));
const BAFOLandingPage = lazy(() => import('@/pages/sourcing/SourcingL2Landings').then(m => ({ default: m.BAFOLandingPage })));
const FrameworkLandingPage = lazy(() => import('@/pages/sourcing/SourcingL2Landings').then(m => ({ default: m.FrameworkLandingPage })));

const BUILT_PROCUREMENT = new Set([
  'scm/taxonomy', 'scm/category-performance', 'scm/spend-cube', 'scm/kraljic', 'scm/strategy-doc', 'scm/market-intel', 'scm/demand', 'scm/pr-consolidation',
  'sourcing/lifecycle', 'sourcing/cockpit', 'sourcing/standup/preferences', 'sourcing/events', 'sourcing/wizard', 'sourcing/wizard/:eventId',
  'sourcing/approval-inbox', 'sourcing/calendar', 'sourcing/doa-matrix',
  'sourcing/profiles', 'sourcing/longlist/:eventId', 'sourcing/templates',
  'sourcing/supplier-invitation', 'sourcing/clarifications', 'sourcing/bid-submission',
  'sourcing/bid-opening', 'sourcing/evaluation', 'sourcing/auction', 'sourcing/bafo',
  'sourcing/framework-agreements', 'sourcing/direct-award', 'sourcing/award-recommendation',
  'sourcing/award-notification', 'sourcing/notifications', 'sourcing/analytics',
  'sourcing/portal-interface', 'sourcing/itt-compliance',
  'srm/registration', 'srm/qualification', 'srm/performance',
  'srm/relationship', 'srm/diversity', 'srm/collaboration', 'srm/data-governance',
  'p2p',
]);
const BUILT_CONTRACTS = new Set([
  'cm/repository', 'cm/obligation-tracking', 'cm/kpi-framework',
  'cm/clause-library', 'cm/collaborative-editing', 'cm/standard-reports',
  'cm/financial-monitoring', 'cm/health-score', 'cm/contract-closure',
  'cm/change-order-wizard',
  // Iter-137 wiring — all CLM nav routes now resolve to real pages
  'cm/contract-types', 'cm/templates', 'cm/clause-architecture',
  'cm/ai-drafting', 'cm/ai-generation', 'cm/self-review', 'cm/internal-review',
  'cm/multi-stakeholder', 'cm/legal-review', 'cm/external-negotiation',
  'cm/redline-exchange', 'cm/playbooks', 'cm/negotiation',
  'cm/esign', 'cm/approval-signing',
  'cm/ai-obligation', 'cm/obligation-categories', 'cm/obligations',
  'cm/performance', 'cm/amendments', 'cm/amendment-types',
  'cm/amendment-workflow', 'cm/cost-impact', 'cm/variation-log',
  'cm/expiry-alerts', 'cm/renewal-intelligence', 'cm/renewal',
  'cm/portfolio', 'cm/bonds', 'cm/insurance',
  'cm/request-form', 'cm/risk-assessment', 'cm/integrations',
]);
const BUILT_CLAIMS_LD = new Set([
  'cld/register', 'cld/manual-initiation',
  'cld/fm-notice', 'cld/evidence-repository',
  'cld/quantum-engine', 'cld/adjudication',
  'cld/quantum-workbench', 'cld/negotiation-table',
  'cld/enforcement-board', 'cld/recovery-ledger',
  'cld/v2', 'cld/fm-notice-v2', 'cld/evidence-v2', 'cld/portfolio-analytics',
  // Iter-157
  'cld/ld-clause-library', 'cld/ld-config', 'cld/ld-notice', 'cld/ld-recovery',
  'cld/predictive-risk', 'cld/adjudication-list',
]);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function StrategyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/scm/strategy/${id}`} replace />;
}

function ContractRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/cm/repository/${id}`} replace />;
}

function ClaimIdRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/cld/register/${id}`} replace />;
}

function LDRedirect() {
  const { contractId } = useParams();
  return <Navigate to={`/app/cld/ld-config/${contractId}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const seenPaths = new Set();
  const mkPlaceholder = (child, builtSet) => {
    const p = child.path.replace('/app/', '').split('?')[0];
    if (builtSet.has(p) || seenPaths.has(p)) return null;
    seenPaths.add(p);
    return <Route key={p} path={p} element={<PlaceholderPage />} />;
  };
  const procurementPlaceholders = PRE_AWARD_MODULES.flatMap(mod =>
    mod.children.map(c => mkPlaceholder(c, BUILT_PROCUREMENT)).filter(Boolean));
  const contractPlaceholders = CONTRACTS_MODULES.flatMap(mod =>
    mod.children.map(c => mkPlaceholder(c, BUILT_CONTRACTS)).filter(Boolean));
  const claimsLdPlaceholders = CLAIMS_LD_MODULES.flatMap(mod =>
    mod.children.map(c => mkPlaceholder(c, BUILT_CLAIMS_LD)).filter(Boolean));

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen bg-background" data-testid="route-suspense-fallback">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/trial-signup" element={<TrialSignupPage />} />
      <Route path="/superadmin" element={<ProtectedRoute><SuperadminPortal /></ProtectedRoute>} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/portal" element={<SupplierPortalPage />} />
      <Route path="/portal-v2" element={<SupplierPortalV2Page />} />

      {/* SP-10 Unified Supplier Portal (greenfield, iter-166) */}
      <Route path="/sp10/login" element={<SP10Login />} />
      <Route path="/sp10/demo-guide" element={<SP10DemoGuide />} />
      <Route path="/sp10" element={<SP10Layout />}>
        <Route index element={<Navigate to="/sp10/dashboard" replace />} />
        <Route path="dashboard" element={<SP10Dashboard />} />
        <Route path="company" element={<SP10MyCompany />} />
        <Route path="sourcing" element={<SP10Sourcing />} />
        <Route path="purchase-orders" element={<SP10PurchaseOrders />} />
        <Route path="deliveries" element={<SP10Deliveries />} />
        <Route path="invoices" element={<SP10Invoices />} />
        <Route path="contracts" element={<SP10Contracts />} />
        <Route path="claims" element={<SP10Claims />} />
        <Route path="catalogue" element={<SP10Catalogue />} />
      </Route>
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />

        {/* Admin Panel (superadmin only) */}
        <Route path="admin" element={<AdminPanelPage />} />
        <Route path="admin/integrations" element={<IntegrationsAdminPage />} />
        <Route path="admin/erp-connectors" element={<ERPConnectorsPage />} />
        <Route path="admin/form-config" element={<FormConfigAdminPage />} />
        <Route path="admin/import-master-data" element={<ImportMasterDataPage />} />
        <Route path="admin/data-lineage" element={<DataLineagePage />} />
        <Route path="admin/impact-analysis" element={<ImpactAnalysisPage />} />
        <Route path="srm/risk" element={<WithAgentBanner moduleKey="srm5"><RiskManagementPage /></WithAgentBanner>} />
        <Route path="srm/data-governance" element={<WithAgentBanner moduleKey="srm6"><DataGovernancePage /></WithAgentBanner>} />
        <Route path="srm/diversity" element={<WithAgentBanner moduleKey="srm7"><DiversityProgrammeHubPage /></WithAgentBanner>} />
        <Route path="srm/collaboration" element={<WithAgentBanner moduleKey="srm8"><CollaborationHubPage /></WithAgentBanner>} />
        <Route path="admin/system-config" element={<SystemConfigPage />} />

        {/* Procurement: Built modules */}
        <Route path="scm/taxonomy" element={<WithAgentBanner moduleKey="scm1"><TaxonomyPage /></WithAgentBanner>} />
        <Route path="scm/category-performance" element={<WithAgentBanner moduleKey="scm2"><CategoryPerformancePage /></WithAgentBanner>} />
        <Route path="scm/spend-cube" element={<WithAgentBanner moduleKey="scm2"><SpendAnalyticsPage /></WithAgentBanner>} />
        <Route path="scm/kraljic" element={<WithAgentBanner moduleKey="scm3"><KraljicMatrixPage /></WithAgentBanner>} />
        <Route path="scm/strategy-doc" element={<WithAgentBanner moduleKey="scm4"><CategoryStrategyPage /></WithAgentBanner>} />
        <Route path="scm/market-intel" element={<WithAgentBanner moduleKey="scm5"><MarketIntelPage /></WithAgentBanner>} />
        <Route path="scm/demand" element={<WithAgentBanner moduleKey="scm6"><DemandPlanningPage /></WithAgentBanner>} />
        <Route path="scm/pr-consolidation" element={<WithAgentBanner moduleKey="scm2"><PRConsolidationPage /></WithAgentBanner>} />
        <Route path="scm/strategy" element={<PreAwardPage />} />
        <Route path="scm/strategy/:id" element={<StrategyDetailPage />} />
        <Route path="sourcing/lifecycle" element={<TenderManagementPage />} />
        <Route path="sourcing/cockpit" element={<SourcingCockpitPage />} />
        <Route path="sourcing/standup/preferences" element={<StandupPreferencesPage />} />
        {/* Supplier Management — Built L2 pages */}
        <Route path="srm/registration" element={<WithAgentBanner moduleKey="srm1"><SupplierRegistrationPage /></WithAgentBanner>} />
        <Route path="srm/vendors" element={<WithAgentBanner moduleKey="srm3"><VendorsPage /></WithAgentBanner>} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="srm/qualification" element={<WithAgentBanner moduleKey="srm2"><QualificationPage /></WithAgentBanner>} />
        <Route path="srm/performance" element={<WithAgentBanner moduleKey="srm3"><PerformancePage /></WithAgentBanner>} />
        <Route path="srm/relationship" element={<WithAgentBanner moduleKey="srm4"><RelationshipManagementPage /></WithAgentBanner>} />
        <Route path="srm/extras" element={<SRMExtrasPage />} />
        <Route path="sourcing/extras" element={<SourcingExtrasPage />} />
        <Route path="requisitions/extras" element={<RequisitionsExtrasPage />} />
        <Route path="po/extras" element={<POExtrasPage />} />
        <Route path="grn/extras" element={<GRExtrasPage />} />
        <Route path="bridges/extras" element={<BridgeExtrasPage />} />
        {/* PR creation + management lives EXCLUSIVELY in the Requisitions module */}
        <Route path="p2p" element={<Navigate to="/app/requisitions" replace />} />
        <Route path="p2p/create" element={<Navigate to="/app/requisitions?tab=create" replace />} />
        <Route path="pr/creation" element={<RequisitionsPage />} />
        <Route path="pr/nlp-intake" element={<RequisitionsPage />} />
        <Route path="pr/ai-enrichment" element={<RequisitionsPage />} />
        <Route path="pr/compliance" element={<RequisitionsPage />} />
        <Route path="pr/approval-workflow" element={<RequisitionsPage />} />
        <Route path="pr/approval-matrix" element={<RequisitionsPage />} />
        <Route path="pr/approver" element={<RequisitionsPage />} />
        <Route path="requisitions" element={<RequisitionsPage />} />
        <Route path="po" element={<PurchaseOrdersPage />} />
        <Route path="po/calloff" element={<POExtrasPage />} />
        <Route path="po/create" element={<PurchaseOrdersPage />} />
        <Route path="po/types" element={<PurchaseOrdersPage />} />
        <Route path="po/automation" element={<PurchaseOrdersPage />} />
        <Route path="po/terms" element={<PurchaseOrdersPage />} />
        <Route path="po/communication" element={<PurchaseOrdersPage />} />
        <Route path="po/amendments" element={<PurchaseOrdersPage />} />
        <Route path="po/tracking" element={<PurchaseOrdersPage />} />
        <Route path="po/analytics" element={<PurchaseOrdersPage />} />
        <Route path="po/copilot" element={<PurchaseOrdersPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="grn" element={<GoodsReceiptPage />} />
        <Route path="grn/asn" element={<GoodsReceiptPage />} />
        <Route path="grn/receipt" element={<GoodsReceiptPage />} />
        <Route path="grn/inspection" element={<GoodsReceiptPage />} />
        <Route path="grn/returns" element={<GoodsReceiptPage />} />
        <Route path="grn/ses" element={<GoodsReceiptPage />} />
        <Route path="grn/analytics" element={<GoodsReceiptPage />} />
        <Route path="goods-receipt" element={<GoodsReceiptPage />} />
        <Route path="suppliers" element={<Navigate to="/app/srm/qualification" replace />} />
        <Route path="suppliers/:supplierId/360" element={<SupplierThreeSixtyPage />} />
        <Route path="invoices" element={<InvoiceWorkbenchPage />} />
        <Route path="invoices/admin" element={<InvoiceAdminPage />} />
        <Route path="invoices/mobile" element={<MobileApprovalPage />} />
        <Route path="invoices/legacy" element={<InvoicesPage />} />
        <Route path="invoices/exceptions" element={<InvoiceWorkbenchPage />} />
        <Route path="invoices/payments" element={<InvoiceWorkbenchPage />} />
        <Route path="catalogue" element={<CataloguePage />} />
        <Route path="catalogue/internal" element={<CataloguePage />} />
        <Route path="catalogue/punchout" element={<CataloguePage />} />
        <Route path="srm/portal-questionnaire/:questionnaireId/:recordId" element={<SupplierQuestionnairePageWrapper />} />
        <Route path="srm/assessment-checklist/:assessmentId" element={<AssessmentChecklistPage />} />
        <Route path="sourcing/events" element={<SourcingEventsPage />} />
        <Route path="sourcing/wizard" element={<SourcingEventWizard />} />
        <Route path="sourcing/wizard/:eventId" element={<SourcingEventWizard />} />
        <Route path="sourcing/approval-inbox" element={<ApprovalInboxPage />} />
        <Route path="sourcing/calendar" element={<SourcingCalendarPage />} />
        <Route path="sourcing/doa-matrix" element={<DOAMatrixAdminPage />} />
        <Route path="sourcing/profiles" element={<EventTypeProfilesAdminPage />} />
        <Route path="sourcing/longlist/:eventId" element={<LongListReviewPage />} />
        <Route path="sourcing/templates" element={<EventTemplatesPage />} />
        <Route path="sourcing/supplier-invitation" element={<SupplierInvitationPage />} />
        <Route path="sourcing/clarifications/:eventId" element={<ClarificationDashboard />} />
        <Route path="sourcing/clarifications" element={<ClarificationHubPage />} />
        <Route path="sourcing/bid-submission/:eventId" element={<BidMonitoringDashboard />} />
        <Route path="sourcing/bid-submission" element={<BidMonitoringHubPage />} />
        <Route path="portal/bid-submission/:eventId" element={<PortalBidSubmissionPage />} />
        <Route path="portal/capa" element={<PortalCAPAPage />} />
        <Route path="portal/invoices" element={<PortalInvoicesPage />} />
        <Route path="admin/workflows" element={<WorkflowsAdminPage />} />
        <Route path="portal/auction/:eventId" element={<PortalAuctionPage />} />
        <Route path="portal/bafo/:bafoEventId" element={<PortalBAFOPage />} />
        <Route path="portal/frameworks" element={<PortalFrameworkPage />} />
        <Route path="sourcing/bid-submission" element={<SourcingL2StubPage />} />
        <Route path="sourcing/bid-opening/:eventId" element={<BidOpeningPage />} />
        <Route path="sourcing/bid-opening" element={<BidOpeningLandingPage />} />
        <Route path="sourcing/evaluation/:eventId" element={<EvaluationPage />} />
        <Route path="sourcing/evaluation" element={<EvaluationLandingPage />} />
        <Route path="sourcing/auction/:eventId" element={<AuctionPage />} />
        <Route path="sourcing/auction" element={<AuctionLandingPage />} />
        <Route path="sourcing/bafo/:eventId" element={<BAFOPage />} />
        <Route path="sourcing/bafo" element={<BAFOLandingPage />} />
        <Route path="sourcing/framework/:frameworkId" element={<FrameworkPage />} />
        <Route path="sourcing/framework-agreements" element={<FrameworkLandingPage />} />
        <Route path="sourcing/direct-awards" element={<DirectAwardLandingPage />} />
        <Route path="sourcing/direct-awards/:daId" element={<DirectAwardPage />} />
        <Route path="sourcing/direct-award" element={<DirectAwardLandingPage />} />
        <Route path="sourcing/award-recommendation" element={<AwardRecommendationLandingPage />} />
        {/* SS-4.13 / 4.14 / 4.15 / 4.16 — cross-cutting support modules */}
        <Route path="sourcing/communication-engine" element={<CommunicationEnginePage />} />
        <Route path="sourcing/analytics" element={<SourcingAnalyticsPage />} />
        <Route path="sourcing/portal-admin" element={<SupplierPortalAdminPage />} />
        <Route path="sourcing/itt-compliance" element={<IttCompliancePage />} />
        <Route path="sourcing/award-recommendation/:recId" element={<AwardRecommendationPage />} />
        <Route path="sourcing/award-closure/:recId" element={<AwardRecommendationPage />} />
        <Route path="sourcing/award-closure" element={<AwardRecommendationLandingPage />} />
        <Route path="sourcing/award-doa" element={<AwardDOAMatrixPage />} />
        <Route path="sourcing/award-notification" element={<AwardRecommendationLandingPage />} />
        <Route path="sourcing/notifications" element={<SourcingL2StubPage />} />
        <Route path="sourcing/analytics" element={<SourcingL2StubPage />} />
        <Route path="sourcing/portal-interface" element={<SourcingL2StubPage />} />

        {/* Procurement: Placeholders */}
        {procurementPlaceholders}

        {/* Contracts Management: Built modules */}
        <Route path="cm/repository" element={<ContractsPage />} />
        <Route path="cm/repository/new" element={<ContractWizard />} />
        <Route path="cm/repository/:id" element={<ContractDetailPage />} />
        <Route path="cm/obligation-tracking" element={<ObligationsPage />} />
        <Route path="cm/kpi-framework" element={<MilestonesPage />} />
        <Route path="cm/clause-library" element={<ClauseLibraryPage />} />
        <Route path="cm/templates" element={<TemplateBuilderPage />} />
        <Route path="cm/clause-architecture" element={<ClauseLibraryPage />} />
        <Route path="cm/draft/:contractId" element={<ContractDraftPage />} />
        <Route path="cm/ai-drafting" element={<ContractDraftPage />} />
        <Route path="cm/ai-generation" element={<ContractDraftPage />} />
        <Route path="cm/collaborative-editing" element={<ContractDraftPage />} />
        <Route path="cm/self-review" element={<ContractDraftPage />} />
        <Route path="cm/internal-review" element={<ContractDraftPage />} />
        {/* CLM Batch 3 — Phases 11-14 */}
        <Route path="cm/negotiation" element={<ContractNegotiationPage />} />
        <Route path="cm/negotiation/:contractId" element={<ContractNegotiationPage />} />
        <Route path="cm/redlines" element={<ContractNegotiationPage />} />
        <Route path="cm/playbooks" element={<ContractNegotiationPage />} />
        <Route path="cm/esign" element={<ContractNegotiationPage />} />
        <Route path="cm/esign/:contractId" element={<ContractNegotiationPage />} />
        <Route path="cm/obligations" element={<ContractNegotiationPage />} />
        <Route path="cm/obligations/:contractId" element={<ContractNegotiationPage />} />
        <Route path="cm/obligation-extraction" element={<ContractNegotiationPage />} />
        <Route path="cm/obligation-tracking" element={<ContractNegotiationPage />} />
        <Route path="cm/force-majeure" element={<ContractNegotiationPage />} />
        {/* CLM Batch 4 — Phases 15-17 */}
        <Route path="cm/performance" element={<ContractPerformancePage />} />
        <Route path="cm/performance/:contractId" element={<ContractPerformancePage />} />
        <Route path="cm/health-score-v2" element={<ContractPerformancePage />} />
        <Route path="cm/amendments" element={<ContractPerformancePage />} />
        <Route path="cm/amendments/:contractId" element={<ContractPerformancePage />} />
        <Route path="cm/renewal" element={<ContractPerformancePage />} />
        <Route path="cm/renewal/:contractId" element={<ContractPerformancePage />} />
        <Route path="cm/expiry" element={<ContractPerformancePage />} />
        {/* CLM Portfolio dashboard + Bonds + Insurance */}
        <Route path="cm/portfolio" element={<CMPortfolioPage />} />
        <Route path="cm/portfolio/:tab" element={<CMPortfolioPage />} />
        <Route path="cm/bonds" element={<CMPortfolioPage />} />
        <Route path="cm/insurance" element={<CMPortfolioPage />} />
        {/* Iter-137 — Map remaining nav entries to real pages */}
        <Route path="cm/contract-types" element={<ContractTypesReferencePage />} />
        <Route path="cm/request-form" element={<ContractWizard />} />
        <Route path="cm/risk-assessment" element={<ContractDraftPage />} />
        <Route path="cm/multi-stakeholder" element={<ContractDraftPage />} />
        <Route path="cm/legal-review" element={<ContractDraftPage />} />
        <Route path="cm/external-negotiation" element={<ContractNegotiationPage />} />
        <Route path="cm/redline-exchange" element={<ContractNegotiationPage />} />
        <Route path="cm/approval-signing" element={<ContractNegotiationPage />} />
        <Route path="cm/ai-obligation" element={<ContractNegotiationPage />} />
        <Route path="cm/obligation-categories" element={<ContractNegotiationPage />} />
        <Route path="cm/amendment-types" element={<ContractPerformancePage />} />
        <Route path="cm/amendment-workflow" element={<ContractPerformancePage />} />
        <Route path="cm/cost-impact" element={<ContractPerformancePage />} />
        <Route path="cm/variation-log" element={<ContractPerformancePage />} />
        <Route path="cm/expiry-alerts" element={<CMPortfolioPage />} />
        <Route path="cm/renewal-intelligence" element={<ContractPerformancePage />} />
        <Route path="cm/integrations" element={<ContractIntegrationsPage />} />
        <Route path="cm/integrations/:contractId" element={<ContractIntegrationsPage />} />
        <Route path="cm/collaborative-editing" element={<CorrespondencePage />} />
        <Route path="cm/standard-reports" element={<ReportsPage />} />
        <Route path="cm/financial-monitoring" element={<ContractPerformancePage />} />
        <Route path="cm/health-score" element={<ContractPerformancePage />} />
        <Route path="cm/contract-closure" element={<CMPortfolioPage />} />
        <Route path="cm/change-order-wizard" element={<ChangeOrderWizardPage />} />

        {/* Contracts Management: Placeholders */}
        {contractPlaceholders}

        {/* Claims & LD Management: Built modules */}
        <Route path="cld/register" element={<ClaimsPage />} />
        <Route path="cld/register/:id" element={<ClaimDetailPage />} />
        <Route path="cld/manual-initiation" element={<ClaimInitiationPage />} />
        <Route path="cld/ld-config/:contractId" element={<LDManagementPage />} />
        {/* Iter-156 audit fix — listed-as-built but had no route handler */}
        <Route path="cld/ld-config" element={<LDManagementPage />} />
        {/* Iter-157 — real LD Clause Library + LD Notice Dispatch + Predictive Risk + Case Room */}
        <Route path="cld/ld-clause-library" element={<LDClauseLibraryPage />} />
        <Route path="cld/ld-notice" element={<LDNoticeDispatchPage />} />
        <Route path="cld/ld-recovery" element={<Navigate to="/app/cld/recovery-ledger" replace />} />
        <Route path="cld/predictive-risk" element={<PredictiveRiskPage />} />
        <Route path="cld/adjudication-list" element={<AdjudicationListPage />} />
        <Route path="cld/adjudication/case/:caseRef" element={<AdjudicationCaseRoomPage />} />

        {/* Iter-146 — Claims & LD drill-down pages */}
        <Route path="cld/fm-notice" element={<FMNoticePage />} />
        <Route path="cld/evidence-repository" element={<EvidenceRepoPage />} />
        <Route path="cld/quantum-engine" element={<QuantumEnginePage />} />
        <Route path="cld/adjudication" element={<DisputeBoardPage />} />

        {/* Iter-150 — 4 authoring pages for Claims & LD sub-modules */}
        <Route path="cld/quantum-workbench" element={<QuantumWorkbench />} />
        <Route path="cld/negotiation-table" element={<NegotiationTable />} />
        <Route path="cld/enforcement-board" element={<EnforcementBoard />} />
        <Route path="cld/recovery-ledger" element={<RecoveryLedger />} />
        {/* Iter-155 — v2 dedicated pages for new CLD Phase 3-15 endpoints */}
        <Route path="cld/v2" element={<CldV2Workbench />} />
        <Route path="cld/portfolio-analytics" element={<CldPortfolioAnalytics />} />
        <Route path="cld/fm-notice-v2" element={<FmNoticeV2 />} />
        <Route path="cld/evidence-v2" element={<EvidenceRepoV2 />} />

        {/* Iter-151 — XMOD admin + SP-10 portal contracts */}
        <Route path="admin/xmod-health" element={<XmodHealthDashboard />} />
        <Route path="admin/audit-trail-explorer" element={<AuditTrailExplorerPage />} />
        <Route path="cxo-dashboard" element={<CxoDashboardPage />} />
        <Route path="portal/contracts" element={<PortalContractsPage />} />
        {/* Aliases — common siblings route to the same drill-down */}
        <Route path="cld/event-detection" element={<Navigate to="/app/cld/v2" replace />} />
        <Route path="cld/fm-notice" element={<Navigate to="/app/cld/fm-notice-v2" replace />} />
        <Route path="cld/fm-investigation" element={<Navigate to="/app/cld/fm-notice-v2" replace />} />
        <Route path="cld/fm-quantification" element={<Navigate to="/app/cld/quantum-workbench" replace />} />
        <Route path="cld/initiation/new" element={<Navigate to="/app/cld/manual-initiation" replace />} />
        <Route path="cld/evidence-framework" element={<Navigate to="/app/cld/evidence-v2" replace />} />
        <Route path="cld/evidence-types" element={<Navigate to="/app/cld/evidence-v2" replace />} />
        <Route path="cld/evidence-repository" element={<Navigate to="/app/cld/evidence-v2" replace />} />
        <Route path="cld/evidence-quality" element={<Navigate to="/app/cld/evidence-v2" replace />} />
        <Route path="cld/loss-quantification" element={<Navigate to="/app/cld/quantum-workbench" replace />} />
        <Route path="cld/ai-quantum" element={<Navigate to="/app/cld/quantum-workbench" replace />} />
        <Route path="cld/ld-clause-types" element={<Navigate to="/app/cld/ld-clause-library" replace />} />
        <Route path="cld/ld-computation" element={<Navigate to="/app/cld/v2" replace />} />
        <Route path="cld/delay-ld" element={<Navigate to="/app/cld/v2" replace />} />
        <Route path="cld/kpi-ld" element={<Navigate to="/app/cld/v2" replace />} />
        <Route path="cld/multi-party-ld" element={<Navigate to="/app/cld/ld-config" replace />} />
        <Route path="cld/ai-ld-notice" element={<Navigate to="/app/cld/ld-notice" replace />} />
        <Route path="cld/ld-deduction" element={<Navigate to="/app/cld/recovery-ledger" replace />} />
        <Route path="cld/dispute-classification" element={<Navigate to="/app/cld/adjudication" replace />} />
        <Route path="cld/dispute-types" element={<Navigate to="/app/cld/adjudication" replace />} />
        <Route path="cld/dispute-resolution" element={<Navigate to="/app/cld/adjudication" replace />} />
        <Route path="cld/commercial-negotiation" element={<Navigate to="/app/cld/negotiation-table" replace />} />
        <Route path="cld/arbitration" element={<Navigate to="/app/cld/adjudication" replace />} />
        <Route path="cld/counter-claims" element={<Navigate to="/app/cld/v2" replace />} />
        <Route path="cld/analytics-dashboard" element={<Navigate to="/app/cld/portfolio-analytics" replace />} />

        {/* Claims & LD Management: Placeholders */}
        {claimsLdPlaceholders}

        {/* Commercial standalone */}
        <Route path="variations" element={<Navigate to="/app/cm/change-order-wizard" replace />} />
        <Route path="system-config" element={<SystemConfigPage />} />

        {/* v3.0 Navigation Restructure — redirects for removed/merged modules */}
        {/* Spend Analytics sidebar item — absorbed into Category & Spend */}
        <Route path="spend" element={<Navigate to="/app/scm/spend-cube" replace />} />
        <Route path="spend/foundation" element={<Navigate to="/app/scm/spend-cube" replace />} />
        <Route path="spend/analytics" element={<Navigate to="/app/scm/spend-cube" replace />} />
        <Route path="spend/ai-insights" element={<Navigate to="/app/scm/spend-cube" replace />} />
        {/* Compliance & Audit — distributed to module compliance tabs + Settings */}
        <Route path="compliance" element={<Navigate to="/app/system-config" replace />} />
        <Route path="compliance/sod" element={<Navigate to="/app/system-config" replace />} />
        <Route path="compliance/monitoring" element={<Navigate to="/app/system-config" replace />} />
        <Route path="compliance/audit" element={<Navigate to="/app/system-config" replace />} />
        {/* Reports & KPIs — absorbed into Dashboard */}
        <Route path="reporting" element={<Navigate to="/app" replace />} />
        <Route path="reporting/library" element={<Navigate to="/app" replace />} />
        <Route path="reporting/kpis" element={<Navigate to="/app" replace />} />
        {/* CLM Analytics & KPIs — absorbed into Dashboard + module-native tabs */}
        <Route path="cm/portfolio-dashboard" element={<Navigate to="/app/cm/repository" replace />} />
        <Route path="cm/cycle-analytics" element={<Navigate to="/app/cm/repository" replace />} />
        <Route path="cm/clm-kpis" element={<Navigate to="/app" replace />} />
        {/* Force Majeure standalone — merged into Claim Initiation as a claim trigger */}
        <Route path="cld/fm-clause-analysis" element={<Navigate to="/app/cld/manual-initiation?type=FORCE_MAJEURE" replace />} />
        <Route path="cld/fm-workflow" element={<Navigate to="/app/cld/manual-initiation?type=FORCE_MAJEURE" replace />} />
        <Route path="cld/fm-negotiation" element={<Navigate to="/app/cld/manual-initiation?type=FORCE_MAJEURE" replace />} />
        {/* Claims Analytics & KPIs — absorbed into Dispute Management analytics + Dashboard */}
        <Route path="cld/improvement-intelligence" element={<Navigate to="/app/cld/analytics-dashboard" replace />} />
        <Route path="cld/reports-kpis" element={<Navigate to="/app" replace />} />

        {/* Legacy redirects: Procurement */}
        <Route path="strategy" element={<Navigate to="/app/scm/strategy" replace />} />
        <Route path="strategy/:id" element={<StrategyRedirect />} />
        <Route path="tenders" element={<Navigate to="/app/sourcing/lifecycle" replace />} />

        {/* Legacy redirects: Contracts */}
        <Route path="contracts" element={<Navigate to="/app/cm/repository" replace />} />
        <Route path="contracts/new" element={<Navigate to="/app/cm/repository/new" replace />} />
        <Route path="contracts/:id" element={<ContractRedirect />} />
        <Route path="obligations" element={<Navigate to="/app/cm/obligation-tracking" replace />} />
        <Route path="milestones" element={<Navigate to="/app/cm/kpi-framework" replace />} />
        <Route path="correspondence" element={<Navigate to="/app/cm/collaborative-editing" replace />} />
        <Route path="documents" element={<Navigate to="/app/cm/clause-library" replace />} />
        <Route path="reports" element={<Navigate to="/app/cm/standard-reports" replace />} />
        <Route path="payments" element={<Navigate to="/app/cm/financial-monitoring" replace />} />
        <Route path="risks" element={<Navigate to="/app/cm/health-score" replace />} />
        <Route path="vendors" element={<Navigate to="/app/cm/contract-closure" replace />} />

        {/* Legacy redirects: Claims & LD */}
        <Route path="claims" element={<Navigate to="/app/cld/register" replace />} />
        <Route path="claims/:id" element={<ClaimIdRedirect />} />
        <Route path="ld" element={<Navigate to="/app/cld/ld-clause-library" replace />} />
        <Route path="ld/:contractId" element={<LDRedirect />} />
        {/* Catch-all: redirect any unknown /app/* route to the dashboard so users never see a blank screen */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" theme="light" richColors />
      </AuthProvider>
    </div>
  );
}

// Wrapper that pulls URL params for the questionnaire page
function SupplierQuestionnairePageWrapper() {
  const { questionnaireId, recordId } = useParams();
  return <SupplierQuestionnairePage questionnaireId={questionnaireId} recordId={recordId} />;
}

export default App;
